import { GroupParticipant } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { getMessageTypeFromRelatedIds } from './get-message-type-from-related-ids';
import { getRelatedIdsThatMeetRequirements, RelatedIDWithRequirement } from './get-related-ids-that-meet-requirements';

type ScheduleRequest = ManualSmsScheduledV1.Types.ScheduleIO['input'];
type ScheduleGroupRequest = ManualSmsScheduledV1.Types.ScheduleGroupSMSIO['input'];

type ScheduleMutationLike = {
  mutateAsync: (
    request: ScheduleRequest,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => Promise<ManualSmsScheduledV1.Types.ScheduleIO['output']>;
};

type ScheduleGroupMutationLike = {
  mutateAsync: (
    request: ScheduleGroupRequest,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => Promise<ManualSmsScheduledV1.Types.ScheduleGroupSMSIO['output']>;
};

export type BuildScheduleRequestArgs = Pick<
  ScheduleRequest,
  'body' | 'relatedIds' | 'sendAt' | 'pausable' | 'mediaIds'
> & {
  groupId: string;
  threadId: string;
  locationPhone: string;
  personPhone: string;
  contextPersonId?: string;
  primaryContactId?: string;
  userId?: string;
};

export const buildScheduleRequest = ({
  groupId,
  threadId,
  locationPhone,
  personPhone,
  contextPersonId,
  primaryContactId,
  userId,
  body,
  relatedIds,
  sendAt,
  pausable,
  mediaIds,
}: BuildScheduleRequestArgs): ScheduleRequest => {
  return {
    locationId: groupId,
    threadId,
    locationPhone,
    personPhone: formatPhoneNumberE164(personPhone),
    personId: contextPersonId || primaryContactId,
    scheduledBy: userId ?? '',
    messageType: getMessageTypeFromRelatedIds(relatedIds ?? []),
    body,
    relatedIds,
    sendAt,
    pausable,
    mediaIds,
  };
};

export type BuildScheduleGroupRequestArgs = Pick<
  ScheduleGroupRequest,
  'body' | 'relatedIds' | 'sendAt' | 'pausable' | 'mediaIds' | 'messageType'
> & {
  groupId: string;
  threadId: string;
  locationPhone: string;
  groupParticipants?: GroupParticipant[];
  groupName?: string;
  userId?: string;
};

export const buildScheduleGroupRequest = ({
  groupId,
  threadId,
  locationPhone,
  groupParticipants,
  groupName,
  userId,
  body,
  relatedIds,
  sendAt,
  pausable,
  mediaIds,
  messageType,
}: BuildScheduleGroupRequestArgs): ScheduleGroupRequest => {
  return {
    locationId: groupId,
    threadId,
    locationPhone,
    recipients:
      groupParticipants?.reduce<ScheduleGroupRequest['recipients']>((acc, participant) => {
        if (!participant.phoneNumber) return acc;
        acc.push({
          personPhone: formatPhoneNumberE164(participant.phoneNumber),
          personId: participant.person?.personId,
        });
        return acc;
      }, []) ?? [],
    groupName: groupName ?? '',
    scheduledBy: userId ?? '',
    body,
    relatedIds,
    sendAt,
    pausable,
    mediaIds,
    messageType,
  };
};

export type ScheduleMessageContext = {
  isGroupThread: boolean;
  groupParticipants?: GroupParticipant[];
  scheduleGroupSMSMutation: ScheduleGroupMutationLike;
  scheduleMutation: ScheduleMutationLike;
  groupId: string;
  threadId: string;
  locationPhone: string;
  personPhone: string;
  contextPersonId?: string;
  primaryContactId?: string;
  groupName?: string;
  userId?: string;
};

export type ScheduleMessagePayload = {
  body: string;
  mediaIds: string[];
  relatedIds: RelatedIDWithRequirement[];
  sendAt: string;
  pausable?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
};

export const scheduleMessage = (context: ScheduleMessageContext, payload: ScheduleMessagePayload) => {
  const { body, mediaIds, relatedIds, sendAt, pausable, onSuccess, onError } = payload;
  const resolvedRelatedIds = getRelatedIdsThatMeetRequirements({
    relatedIds,
    body,
  });

  if (context.isGroupThread && context.groupParticipants) {
    return context.scheduleGroupSMSMutation.mutateAsync(
      buildScheduleGroupRequest({
        groupId: context.groupId,
        threadId: context.threadId,
        locationPhone: context.locationPhone,
        groupParticipants: context.groupParticipants,
        groupName: context.groupName,
        userId: context.userId,
        body,
        mediaIds,
        relatedIds: resolvedRelatedIds,
        messageType: getMessageTypeFromRelatedIds(resolvedRelatedIds),
        sendAt,
        pausable,
      }),
      {
        onSuccess,
        onError,
      }
    );
  }

  return context.scheduleMutation.mutateAsync(
    buildScheduleRequest({
      groupId: context.groupId,
      threadId: context.threadId,
      locationPhone: context.locationPhone,
      personPhone: context.personPhone,
      contextPersonId: context.contextPersonId,
      primaryContactId: context.primaryContactId,
      userId: context.userId,
      body,
      mediaIds,
      relatedIds: resolvedRelatedIds,
      sendAt,
      pausable,
    }),
    {
      onSuccess,
      onError,
    }
  );
};
